import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../WizardLayout';
import Stepper from '../Stepper';
import { updateOnboardingSession } from '../../../services/wizardApi';
import { toast } from 'react-toast';
import useProjectSocket from '../../../hooks/useProjectSocket';
import axiosInstance from '../../../api/axiosInstance';
import {
  LuSparkles, LuVideo, LuRadar,
  LuCheck, LuInfo, LuLoader
} from 'react-icons/lu';
import '../../../styles/wizard.css';
import { OnboardingModal, OnboardingAlert } from '../onboarding';
import { INTENT_TYPES, STEP_STATUS, TEST_CREATION_MODES } from '../../../Config/Onboarding/constant';

const STEP5_BACK_TOAST =
  'Previous step is already complete. Continue here, or use Save & Exit to terminate current progress';

const Step5TestCreationMode = () => {
  const navigate = useNavigate();
  const {
    engine,
    data,
    project,
    intent,
    testCreationMode,
    setTestCreationMode,
    setRecorder,
    updateStepFlowByMode,
    nextStep,
    previousStep,
    sessionId,
    markStepCompleted,
    setStepStatus,
    goToStep
  } = useOnboarding();
  
  const [selectedMode, setSelectedMode] = useState(testCreationMode || null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAILoader, setShowAILoader] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiMessage, setAiMessage] = useState('AI is analyzing your work items...');
  const isRevertingBack = useRef(false);
  // Tracks which Step 6 mode to open after socket hits 100% ('ai' | 'ui')
  const pendingNavModeRef = useRef(null);
  
  // WebSocket integration
  const { 
    status: wsStatus, 
    messages: wsMessages, 
    progress: wsProgress, 
    connectWebSocket, 
    sendEvent 
  } = useProjectSocket(project?.project_id);

  // Check if step 4 is completed (restrict navigation if so)
  const isStep4Completed = engine.flow.completedSteps.includes(4);
  const isGenerateTestsFromJira = intent === INTENT_TYPES.JIRA_REQUIREMENTS;

  // Monitor WebSocket progress
  useEffect(() => {
    if (wsProgress) {
      setAiProgress(wsProgress);

      const isUIDiscoveryFlow = pendingNavModeRef.current === 'ui';
      
      // Update message based on progress
      if (wsProgress < 50) {
        setAiMessage(
          isUIDiscoveryFlow
            ? 'AI is analyzing your requirements...'
            : 'AI is analyzing your work items...'
        );
      } else if (wsProgress < 100) {
        setAiMessage(
          isUIDiscoveryFlow
            ? 'Generating test cases and steps...'
            : 'Generating test cases and steps...'
        );
      }
      
      // Navigate only after generation was started and progress reaches 100%
      if (wsProgress === 100 && pendingNavModeRef.current) {
        const navMode = pendingNavModeRef.current;
        pendingNavModeRef.current = null; // prevent duplicate navigation if progress re-emits
        setTimeout(() => {
          // Mark step 5 as completed and step 6 as in-progress
          markStepCompleted(5);
          setStepStatus(6, STEP_STATUS.IN_PROGRESS);
          
          // Navigate to step 6 with the mode that triggered generation
          navigate(`/app/onboarding?step=6&mode=${navMode}`);
          nextStep();
          toast.success('AI generation completed successfully');
        }, 1000); // Small delay to let user see 100% progress
      }
    }
  }, [wsProgress]);

  // Auto-select AI mode for API projects
  useEffect(() => {
    if (project.applicationType === 'api' && !selectedMode) {
      setSelectedMode(TEST_CREATION_MODES.AI);
    }
  }, [project.applicationType, selectedMode]);

  // Default to RECORDER for WEB/MOBILE projects
  useEffect(() => {
    if ((project.applicationType === 'web' || project.applicationType === 'mobile') && !selectedMode) {
      setSelectedMode(TEST_CREATION_MODES.RECORDER);
    }
  }, [project.applicationType, selectedMode]);

  useEffect(() => {
    const handlePopState = () => {
      // history.go(1) triggers a second popstate — absorb it without another toast
      if (isRevertingBack.current) {
        isRevertingBack.current = false;
        return;
      }

      isRevertingBack.current = true;
      window.history.go(1);
      toast.hideAll();
      toast.info(STEP5_BACK_TOAST);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleModeSelect = (mode) => {
    // If WEB/MOBILE selecting AI, show warning
    if ((project.applicationType === 'web' || project.applicationType === 'mobile') 
        && mode === TEST_CREATION_MODES.AI) {
      setSelectedMode(mode);
      setShowWarningModal(true);
    } else {
      setSelectedMode(mode);
      setShowWarningModal(false);
    }
  };

  const handleContinueWithAI = async () => {
    setShowWarningModal(false);
    await triggerAIGeneration();
  };

  const triggerAIGeneration = async () => {
    const isUIDiscoveryFlow = selectedMode === TEST_CREATION_MODES.UI_DISCOVERY;
    // Set before API call so progress=100% navigates only after generation was started
    pendingNavModeRef.current = isUIDiscoveryFlow ? 'ui' : 'ai';

    setShowAILoader(true);
    setAiProgress(0);
    setAiMessage(
      isUIDiscoveryFlow
        ? 'AI is analyzing your work items...'
        : 'AI is analyzing your requirements...'
    );
    
    try {
      // Step 1: Connect WebSocket
      connectWebSocket();
      
      // Step 2: Wait a moment for connection, then send sync_epics event
      setTimeout(() => {
        console.log('[AI Generation] Sending sync_epics event...');
        sendEvent('sync_epics', {
          project_id: project.project_id,
          progress: 0,
          message: 'Starting test generation...'
        });
      }, 500);

      // Step 3: Call the scenario generation API
      try {
        const response = await axiosInstance.post(
          `/scenario/ai_gen_scenario_project?project_id=${project?.project_id}&is_wizard=true`,
          {}
        );
        
        console.log('[AI Generation] API Response:', response);
        
        if (response.status_code === 202 || response.status === 202) {
          console.log('[AI Generation] Scenario generation started in background');
          // WebSocket will handle the progress updates
        }
      } catch (apiError) {
        console.error('[AI Generation] API Error:', apiError);
        toast.error(apiError.response?.data?.message || 'Failed to start AI generation');
        pendingNavModeRef.current = null;
        setShowAILoader(false);
        setAiProgress(0);
      }
    } catch (error) {
      console.error('Error initiating AI generation:', error);
      toast.error('Failed to start AI generation');
      pendingNavModeRef.current = null;
      setShowAILoader(false);
      setAiProgress(0);
    }
  };

  const handleSwitchToRecorder = () => {
    setSelectedMode(TEST_CREATION_MODES.RECORDER);
    setShowWarningModal(false);
  };

  const handleContinue = async () => {
    if (!selectedMode) {
      toast.error('Please select a test creation mode');
      return;
    }

    // For AI mode with UI projects, we already show warning
    // If user continues with AI from warning or if no warning (API projects), trigger AI generation
    if (selectedMode === TEST_CREATION_MODES.AI) {
      // Check if this is a UI project and no warning shown yet
      const isUIProject = project.applicationType === 'web' || project.applicationType === 'mobile';
      
      if (isUIProject && !showWarningModal) {
        // UI project selecting AI - show warning first
        setShowWarningModal(true);
        return;
      } else if (isUIProject && showWarningModal) {
        // Already confirmed in warning modal, proceed with AI
        await triggerAIGeneration();
        return;
      } else {
        // API project with AI - no warning needed, proceed directly
        setTestCreationMode(selectedMode);
        updateStepFlowByMode(selectedMode);
        
        if (sessionId) {
          await updateOnboardingSession(sessionId, {
            currentStep: 5,
            testCreationMode: selectedMode
          });
        }
        
        await triggerAIGeneration();
        return;
      }
    }

    // Auto Discover UI: trigger AI generation, then wait for socket 100% before Step 6
    if (selectedMode === TEST_CREATION_MODES.UI_DISCOVERY) {
      setIsLoading(true);
      try {
        setTestCreationMode(selectedMode);
        updateStepFlowByMode(selectedMode);

        if (sessionId) {
          await updateOnboardingSession(sessionId, {
            currentStep: 5,
            testCreationMode: selectedMode
          });
        }

        await triggerAIGeneration();
      } catch (error) {
        console.error('Error starting UI discovery generation:', error);
        toast.error('Failed to start UI discovery. Please try again.');
        pendingNavModeRef.current = null;
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Non-AI modes follow original flow
    setIsLoading(true);
    try {
      setTestCreationMode(selectedMode);
      updateStepFlowByMode(selectedMode);
      
      if (selectedMode === TEST_CREATION_MODES.RECORDER) {
        setRecorder({
          agentConnected: false,
          extensionConnected: false,
          recordingSessionId: null,
          recordingStatus: null,
          capturedSteps: [],
          projectUrl: ''
        });
      }
      
      if (sessionId) {
        await updateOnboardingSession(sessionId, {
          currentStep: 5,
          testCreationMode: selectedMode
        });
      }

      markStepCompleted(5);
      nextStep();
      
      if (selectedMode === TEST_CREATION_MODES.RECORDER) {
        navigate('/app/onboarding?step=6&mode=recorder');
      }

      toast.success('Test creation mode selected');
    } catch (error) {
      console.error('Error saving test creation mode:', error);
      toast.error('Failed to save selection. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isAPIProject = project.applicationType === 'api';
  const isUIProject = project.applicationType === 'web' || project.applicationType === 'mobile';

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={5} completedSteps={[1, 2, 3, 4]} />
        
        <WizardCard
          title="How should Robitest create your tests?"
          subtitle="Choose the approach that best fits your project. You can use other methods later."
        >
          <OnboardingAlert tone="info" title={`Project type: ${project.applicationType?.toUpperCase() || '—'}`}>
            <p>
              {isAPIProject && 'AI can generate tests and scripts directly from requirements.'}
              {isUIProject && 'UI automation requires application structure (DOM/UI Map).'}
              {!isAPIProject && !isUIProject && 'Choose the approach that best fits your application.'}
            </p>
          </OnboardingAlert>

          <div className="ob-choice-grid ob-choice-grid--3" role="group" aria-label="Test creation approach">
            {[
              {
                mode: TEST_CREATION_MODES.AI,
                Icon: LuSparkles,
                iconTone: '',
                title: 'Generate test case from work items',
                desc: 'Use AI to turn your requirements into test cases.',
                badge: isAPIProject ? { label: 'Recommended', tone: 'success' } : null,
                points: [
                  { text: 'Fast test case generation' },
                  { text: 'Covers all requirements' },
                  ...(isAPIProject ? [{ text: 'Creates executable scripts' }] : []),
                  ...(isUIProject ? [{ text: 'Scripts need UI Map (record first)', warning: true }] : []),
                ],
              },
              {
                mode: TEST_CREATION_MODES.RECORDER,
                Icon: LuVideo,
                iconTone: 'ob-choice__icon--danger',
                title: 'Record with Extension',
                desc: 'Record your interactions to build tests and UI Map automatically.',
                badge: isUIProject ? { label: 'Recommended for UI', tone: 'success' } : null,
                points: [
                  { text: 'Captures real user flows' },
                  { text: 'Builds UI Map for selectors' },
                  { text: 'Generates reliable scripts' },
                  { text: 'Best for UI automation' },
                ],
              },
              {
                mode: TEST_CREATION_MODES.UI_DISCOVERY,
                Icon: LuRadar,
                iconTone: 'ob-choice__icon--accent',
                title: 'Auto Discover UI',
                desc: 'Automatically crawl and map your application structure.',
                badge: { label: 'Beta', tone: 'info' },
                points: [
                  { text: 'No manual recording needed' },
                  { text: 'Discovers all UI elements' },
                  { text: 'Builds comprehensive map' },
                  { text: 'Ideal for large apps' },
                ],
              },
            ].map(({ mode, Icon, iconTone, title, desc, badge, points }) => {
              const isSelected = selectedMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  className={`ob-choice ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => handleModeSelect(mode)}
                  aria-pressed={isSelected}
                >
                  {isSelected && (
                    <span className="ob-choice__check" aria-hidden="true">
                      <LuCheck size={13} strokeWidth={3} />
                    </span>
                  )}
                  {badge && (
                    <span className="ob-choice__badges">
                      <span className={`ob-badge ob-badge--${badge.tone}`}>{badge.label}</span>
                    </span>
                  )}
                  <span className={`ob-choice__icon ${iconTone}`} aria-hidden="true">
                    <Icon size={20} />
                  </span>
                  <span className="ob-choice__body">
                    <span className="ob-choice__title">{title}</span>
                    <span className="ob-choice__desc">{desc}</span>
                  </span>
                  <ul className="ob-choice__list">
                    {points.map((pt) => (
                      <li key={pt.text} className={pt.warning ? 'is-warning' : ''}>
                        {pt.warning ? <LuInfo size={14} /> : <LuCheck size={14} />}
                        {pt.text}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          {/* AI Generation Loader Modal */}
          {showAILoader && (
            <OnboardingModal
              align="center"
              dismissible={false}
              busy
              icon={<LuSparkles size={20} />}
              title={aiMessage}
              description="This may take a few moments..."
            >
              <div style={{ textAlign: 'left', paddingBottom: 24 }}>
                <div
                  className="ob-progress"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={aiProgress}
                  aria-label="Generation progress"
                >
                  <div className="ob-progress__fill" style={{ width: `${aiProgress}%` }} />
                </div>
                <div className="ob-progress-meta">
                  <span>Creating test cases and steps</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{aiProgress}%</span>
                </div>
              </div>
            </OnboardingModal>
          )}

          {/* Warning Modal for UI + AI */}
          {showWarningModal && (
            <OnboardingModal
              size="lg"
              tone="warning"
              icon={<LuInfo size={20} />}
              title="UI automation needs application structure"
              description={`For ${project.applicationType} projects, AI can generate test cases, but automation scripts require a UI Map (DOM structure, selectors, navigation flows).`}
              onClose={() => setShowWarningModal(false)}
              footer={
                <>
                  <button type="button" className="ob-btn ob-btn--secondary" onClick={handleContinueWithAI}>
                    Continue with AI Only
                  </button>
                  <button type="button" className="ob-btn ob-btn--primary" onClick={handleSwitchToRecorder} data-autofocus>
                    <LuVideo size={16} aria-hidden="true" />
                    Switch to Recording (Recommended)
                  </button>
                </>
              }
            >
              <OnboardingAlert tone="warning" style={{ marginBottom: 0 }}>
                <p>
                  <strong>What this means:</strong> You can generate test cases now, but script generation
                  will be blocked until you record at least one flow or run UI discovery.
                </p>
              </OnboardingAlert>
            </OnboardingModal>
          )}

          <WizardActions
            // showBack={true}
            // onBack={() => {
            //   previousStep();
            //   navigate('/app/onboarding?step=4');
            // }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextLoading={isLoading}
            nextDisabled={!selectedMode}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step5TestCreationMode;